import type { Schemas } from '@npc/api-client'

export type Project = Schemas['Project']
export type HubFilter = 'all' | 'needs_attention' | 'unverified'
export type ListStatus = 'pending' | 'error' | 'success'

export type HubFact = { status: 'unverified' } | { status: 'value'; value: number }

export type HubSummary = {
  projectCount: HubFact
  healthyCount: HubFact
  needsAttentionCount: HubFact
  runningTasks: HubFact
  runningReleases: HubFact
}

const UNVERIFIED: HubFact = { status: 'unverified' }

/** 后端给了健康结论才算核验过。缺字段不能拿「一切正常」顶上。 */
export function hasVerifiedHealth(project: Project): boolean {
  return (
    typeof project.health_text === 'string' &&
    project.health_text.trim() !== '' &&
    project.health_tone != null
  )
}

export function isNeedsAttention(project: Project): boolean {
  return project.health_tone === 'danger' || project.health_tone === 'warn'
}

function allUnverified(): HubSummary {
  return {
    projectCount: UNVERIFIED,
    healthyCount: UNVERIFIED,
    needsAttentionCount: UNVERIFIED,
    runningTasks: UNVERIFIED,
    runningReleases: UNVERIFIED,
  }
}

function nullableFact(value: number | null | undefined): HubFact {
  return value == null ? UNVERIFIED : { status: 'value', value }
}

/**
 * 汇总带只认 `GET /projects/summary`。
 * `null` / 失败 / 还在读 → 未核验。禁止把失败填成 0。
 * `running` 是正在执行且心跳新鲜的开发任务条数；`running_releases` 是尚未终态的发布条数。
 * 查到零条是数字 0。
 */
export function summarizeFromApi(
  status: ListStatus,
  data: Schemas['ProjectListSummary'] | undefined,
): HubSummary {
  if (status !== 'success' || data === undefined) return allUnverified()
  return {
    projectCount: nullableFact(data.total),
    healthyCount: nullableFact(data.healthy),
    needsAttentionCount: nullableFact(data.needs_attention),
    runningTasks: nullableFact(data.running),
    runningReleases: nullableFact(data.running_releases),
  }
}

/**
 * 只从列表推。Hub 不再用这条画汇总带——留给没有 summary 响应时的单测。
 * 运行中任务 / 发布列表上没有字段，永远未核验。
 */
export function summarize(status: ListStatus, items: readonly Project[] | undefined): HubSummary {
  if (status !== 'success' || items === undefined) return allUnverified()

  const healthComplete = items.every(hasVerifiedHealth)
  return {
    projectCount: { status: 'value', value: items.length },
    healthyCount: healthComplete
      ? { status: 'value', value: items.filter((project) => project.health_tone === 'ok').length }
      : UNVERIFIED,
    needsAttentionCount: healthComplete
      ? { status: 'value', value: items.filter(isNeedsAttention).length }
      : UNVERIFIED,
    runningTasks: UNVERIFIED,
    runningReleases: UNVERIFIED,
  }
}

export function summaryHasUnverified(summary: HubSummary): boolean {
  return (
    summary.projectCount.status === 'unverified' ||
    summary.healthyCount.status === 'unverified' ||
    summary.needsAttentionCount.status === 'unverified' ||
    summary.runningTasks.status === 'unverified' ||
    summary.runningReleases.status === 'unverified'
  )
}

export function factNumber(fact: HubFact): number | null {
  return fact.status === 'value' ? fact.value : null
}

export type RowHealth =
  | { kind: 'verified'; text: string; tone: NonNullable<Project['health_tone']> }
  | { kind: 'unverified' }

export function rowHealth(project: Project): RowHealth {
  if (!hasVerifiedHealth(project)) return { kind: 'unverified' }
  return { kind: 'verified', text: project.health_text as string, tone: project.health_tone! }
}

/**
 * 环境 / 最近任务 / 发布 / 资源异常 / 最后活动：有字段就映射到已有 copy，
 * 缺字段或未知枚举写未核验。禁止用 `public_url` 空去伪造「打不开」。
 */

export const TASK_STATE_COPY = {
  draft: 'development_task.state.draft',
  preparing: 'development_task.state.preparing',
  ready_to_claim: 'development_task.state.ready_to_claim',
  running: 'development_task.state.running',
  gating: 'development_task.state.gating',
  pending_approval: 'development_task.state.pending_approval',
  reviewing: 'development_task.state.reviewing',
  ci_running: 'development_task.state.ci_running',
  v2_handoff: 'development_task.state.v2_handoff',
  fix_requested: 'development_task.state.fix_requested',
  ready: 'development_task.state.ready',
  cancelled: 'development_task.state.cancelled',
  failed: 'development_task.state.failed',
} as const

export type TaskStateCopyId = (typeof TASK_STATE_COPY)[keyof typeof TASK_STATE_COPY]

export function rowTaskCopyId(raw: string | null | undefined): TaskStateCopyId | null {
  if (raw == null || raw === '') return null
  return raw in TASK_STATE_COPY ? TASK_STATE_COPY[raw as keyof typeof TASK_STATE_COPY] : null
}

export type ReleaseCopyId =
  | 'pjshell.release.live'
  | 'pjshell.release.failed'
  | 'pjshell.release.publishing'
  | 'pjshell.env.status.rolled_back'

export function rowReleaseCopyId(raw: string | null | undefined): ReleaseCopyId | null {
  if (raw == null || raw === '') return null
  if (raw === 'active') return 'pjshell.release.live'
  if (raw === 'failed') return 'pjshell.release.failed'
  if (raw === 'pending') return 'pjshell.release.publishing'
  if (raw === 'superseded' || raw === 'rolled_back') return 'pjshell.env.status.rolled_back'
  return null
}

export type EnvStatusKind = 'unverified' | 'ready' | 'failed' | 'rolled_back' | 'no_bundle'

export function rowEnvStatus(raw: string | null | undefined): EnvStatusKind {
  if (raw === 'ready' || raw === 'failed' || raw === 'rolled_back') return raw
  if (raw === 'unverified') return 'no_bundle'
  return 'unverified'
}

export type ResourceKind = 'unverified' | 'has' | 'none'

export function rowResourceKind(raw: boolean | null | undefined): ResourceKind {
  if (raw === true) return 'has'
  if (raw === false) return 'none'
  return 'unverified'
}

export function rowActivityAt(raw: string | null | undefined): string | null {
  if (raw == null || raw === '') return null
  const at = new Date(raw)
  return Number.isNaN(at.getTime()) ? null : raw
}

function attentionRank(project: Project): number {
  if (project.health_tone === 'danger') return 0
  if (project.health_tone === 'warn') return 1
  return 2
}

/** 要处理的排最前。不按名字。同分保持原顺序（stable sort）。 */
export function sortProjects(items: readonly Project[]): Project[] {
  return items
    .map((project, index) => ({ project, index }))
    .sort((a, b) => attentionRank(a.project) - attentionRank(b.project) || a.index - b.index)
    .map((entry) => entry.project)
}

export function matchesQuery(project: Project, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true
  return [project.name, project.slug, project.summary].some((value) =>
    value.toLowerCase().includes(needle),
  )
}

export function matchesFilter(project: Project, filter: HubFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'needs_attention') return isNeedsAttention(project)
  return !hasVerifiedHealth(project)
}

export function visibleProjects(
  items: readonly Project[],
  query: string,
  filter: HubFilter,
): Project[] {
  return sortProjects(items.filter((project) => matchesQuery(project, query) && matchesFilter(project, filter)))
}
